import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { UserCoworkerJSON } from "users/information/coworker-information/_schemas/user-coworker-schema";

/**
 * "Add all colleagues", as rules rather than as a component.
 *
 * The button exists because of a rule the onboarding article states plainly — nobody can bill for
 * work unless they are on the team — and because for a small firm, adding six people one at a time
 * to say "all of them" is friction with no purpose.
 *
 * Everything here is a decision about whose roster this is, who is already on it, and what the
 * person pressing the button is committing to. Each is worth a test of its own, and a module-level
 * function is also one the React Compiler never has to lower.
 */

/**
 * How many colleagues one request asks for.
 *
 * `users/coworkers` caps nothing, so this is the browser's choice alone. Bigger than the roster
 * pickers' 25 because this is one press rather than a page someone is reading, and small enough
 * that a firm of ordinary size is one round trip.
 */
export const COLLEAGUE_PAGE_SIZE = 100;

/**
 * The most pages this will ask for before it stops and says the list was cut short.
 *
 * A bound rather than a limit anyone is expected to reach: five thousand colleagues is far past any
 * firm on the platform, and a loop that trusts a `totalElements` the server got wrong would
 * otherwise never end.
 */
export const MAX_COLLEAGUE_PAGES = 50;

/**
 * Colleagues stay in the shape the API sent them.
 *
 * `UserCoworker.fromJSON` builds a `Name`, and `NameSchema` refuses an empty first or last name — so
 * deserializing the list would throw on the first colleague who has not filled their profile in, and
 * one such person would cost the firm the whole press. Nothing on this path needs a name: a team
 * member is a user id and a legal entity id, and the roster reads names from its own lookup.
 */
export type ColleagueRoster = {
  colleagues: UserCoworkerJSON[];
  /** Whether the firm has more colleagues than {@link MAX_COLLEAGUE_PAGES} would fetch. */
  truncated: boolean;
};

export type ColleaguePageFetch = (pagination: PaginationParameters) => Promise<Paginated<UserCoworkerJSON>>;

/**
 * Every colleague, not the first page of them.
 *
 * `users/coworkers` is paginated and defaults to 25. A button that promised "all colleagues" and
 * added the first 25 would be worse than no button: the roster would look complete and the people
 * missing from it are the ones who then cannot bill.
 */
export async function fetchAllColleagues(fetchPage: ColleaguePageFetch): Promise<ColleagueRoster> {
  const colleagues: UserCoworkerJSON[] = [];
  let pageIndex = 0;
  let truncated = false;

  for (;;) {
    const page = await fetchPage(
      PaginationParameters.create({ pageIndex, pageSize: COLLEAGUE_PAGE_SIZE }),
    );
    colleagues.push(...(page.data ?? []));

    const totalElements = page.headers?.totalElements ?? colleagues.length;
    const isLastPage = (page.data?.length ?? 0) < COLLEAGUE_PAGE_SIZE || colleagues.length >= totalElements;
    if (isLastPage) break;

    pageIndex += 1;
    if (pageIndex >= MAX_COLLEAGUE_PAGES) {
      truncated = true;
      break;
    }
  }

  return { colleagues, truncated };
}

/**
 * The colleagues who are not on this roster already, once each, whether or not a team would take
 * them. The same person twice in one list counts once, and so does a second press: the roster is the
 * set of ids already on the team, and everyone found is added to it as we go.
 */
function newcomers(
  colleagues: readonly UserCoworkerJSON[],
  alreadyOnTeamUserIds: readonly Guid[],
): UserCoworkerJSON[] {
  const seenUserIds: Guid[] = [...alreadyOnTeamUserIds];
  const found: UserCoworkerJSON[] = [];

  for (const colleague of colleagues) {
    if (!colleague.userId || !colleague.legalEntityId) continue;

    const userId = new Guid(colleague.userId);
    if (seenUserIds.some((seen) => seen?.isEqualTo(userId))) continue;

    seenUserIds.push(userId);
    found.push(colleague);
  }

  return found;
}

/**
 * Whether a team would refuse this colleague as a member: their profile lists no professional
 * credentials (#5800). Only the API's `false` says so. A colleague it said nothing about is added,
 * and the API decides when the team is saved.
 */
function lacksCredentials(colleague: UserCoworkerJSON): boolean {
  return colleague.hasCredentials === false;
}

/**
 * The colleagues who are not on this roster already, as team members, leaving out anyone a team
 * would refuse for want of credentials. Adding them anyway would cost the whole save: the API
 * refuses the roster, not the one person, so one colleague with an empty profile would undo the
 * press for everyone else.
 */
export function colleaguesToAdd(
  colleagues: readonly UserCoworkerJSON[],
  alreadyOnTeamUserIds: readonly Guid[],
): EntityMember[] {
  return newcomers(colleagues, alreadyOnTeamUserIds)
    .filter((colleague) => !lacksCredentials(colleague))
    .map((colleague) => new EntityMember({
      userId: new Guid(colleague.userId),
      legalEntityId: new Guid(colleague.legalEntityId),
    }));
}

/**
 * How many colleagues {@link colleaguesToAdd} left out for want of credentials, so the person
 * pressing the button is told that "all" was not all, and why. Somebody already on the team is not
 * counted: nobody is checked twice, and they are not being left out of anything.
 */
export function colleaguesLeftOutForCredentials(
  colleagues: readonly UserCoworkerJSON[],
  alreadyOnTeamUserIds: readonly Guid[],
): number {
  return newcomers(colleagues, alreadyOnTeamUserIds).filter(lacksCredentials).length;
}

/** What is said when every colleague not on the team was left out for want of credentials. */
export function nobodyAddedForCredentialsMessage(leftOutCount: number): string {
  return leftOutCount === 1
    ? "Your one colleague not on this team can't join it until their profile lists professional credentials."
    : `None of the ${leftOutCount} colleagues not on this team can join it until their profiles list `
      + "professional credentials.";
}

/**
 * Whose roster this is, so far as it can be told from who is on it.
 *
 * The leader first, then any member: both sit in the entity whose team this is.
 */
export function rosterLegalEntityId(
  leader: EntityMember | null | undefined,
  members: readonly (EntityMember | null | undefined)[],
): Guid | undefined {
  if (leader?.legalEntityId) return leader.legalEntityId;
  return members.find((member) => member?.legalEntityId)?.legalEntityId;
}

/**
 * Whether this session may add its colleagues to this roster.
 *
 * "Colleagues" are the acting user's own coworkers, so the shortcut only makes sense on a roster
 * belonging to their organization. A proposal's vendor team is editable by the counterparty too —
 * the roster is negotiable content, and a client may propose a change to it — so without this a
 * client pressing the button would put their own staff on the firm's team.
 *
 * Unknown fails closed. An empty roster with no leader is the one case this refuses that it might
 * have allowed, and it is also the one case where nothing on screen says whose team it is.
 */
export function mayAddAllColleagues(
  actingLegalEntityId: Guid | null | undefined,
  rosterEntityId: Guid | null | undefined,
): boolean {
  if (!actingLegalEntityId || !rosterEntityId) return false;
  return actingLegalEntityId.isEqualTo(rosterEntityId);
}

export type AddAllColleaguesPrompt = {
  title: string;
  message: string;
  okButtonText: string;
};

/**
 * What the person pressing the button is told before it happens.
 *
 * A restricted team is the reason this asks at all. `TeamRestricted` is not a rule about who may
 * join — it is a rule about what the roster costs: `AwaitingApprovalByTeamStatus` puts every member
 * on the proposal's Approve list and on its Reject list, so the proposal cannot be hired until all
 * of them have approved, and any one of them can reject it. Adding eleven colleagues to a restricted
 * team therefore turns a hire that needed two signatures into one that needs thirteen. That is a
 * legitimate thing to want and a terrible thing to discover afterwards.
 */
export function addAllColleaguesPrompt(values: {
  additionCount: number;
  teamRestricted?: boolean;
  /** Colleagues left out because their profiles list no professional credentials (#5800). */
  leftOutForCredentialsCount?: number;
}): AddAllColleaguesPrompt {
  const { additionCount, teamRestricted } = values;
  const leftOut = values.leftOutForCredentialsCount ?? 0;
  const people = additionCount === 1 ? "colleague" : "colleagues";
  const base = `Add ${additionCount} ${people} to this team? You can remove anyone before you submit.`;
  const restricted = teamRestricted
    ? ` This team is restricted, so every member has to approve the proposal before it can `
      + `be hired, and any one of them can reject it — these ${additionCount} would each be asked.`
    : "";
  // Said before the press commits, not after: "all" is the button's promise, and it is not keeping it.
  const leftOutSentence = leftOut === 1
    ? " 1 colleague is left out: their profile does not list professional credentials yet, which a team needs."
    : ` ${leftOut} colleagues are left out: their profiles do not list professional credentials yet, which `
      + "a team needs.";

  return {
    title: "Add All Colleagues?",
    message: `${base}${restricted}${leftOut > 0 ? leftOutSentence : ""}`,
    okButtonText: "Add All",
  };
}
